import React, {
  memo, useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import { faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box, IconButton, ListItemButton, Typography,
} from '@mui/material';
import itemTypes from '../itemTypes';
import { DIRECTIONS } from '../../hooks';

const icons = {
  [DIRECTIONS.ascending]: faSortUp,
  [DIRECTIONS.descending]: faSortDown,
};

const hints = {
  [DIRECTIONS.ascending]: 'За зростанням',
  [DIRECTIONS.descending]: 'За спаданням',
};

function OrdersItem({
  name, label, onClick, active, onDblClick, onHover, padded, direction, onDirectionChange, errored,
}) {
  const ref = useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: [itemTypes.availableField, itemTypes.order],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
    hover: (item, monitor) => {
      if (ref.current) {
        const gbcr = ref.current.getBoundingClientRect();
        const cursor = monitor.getClientOffset();
        if ((gbcr.top + gbcr.height / 2) > cursor.y) {
          onHover(name);
        }
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemTypes.order,
    item: { name, type: itemTypes.order },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragProps = useMemo(() => ({
    opacity: isDragging ? 0.4 : 1,
  }), [isDragging]);

  const btnProps = useMemo(() => ({
    pt: padded ? 3 : 0.5,
    transition: 'padding 0.3s ease-out',
    bgcolor: isOver ? 'grey.100' : null,
  }), [isOver, padded]);

  const directionClickHandler = useCallback(
    () => onDirectionChange(
      name,
      direction === DIRECTIONS.ascending ? DIRECTIONS.descending : DIRECTIONS.ascending,
    ),
    [direction, name, onDirectionChange],
  );

  drop(ref);
  return (
    <ListItemButton
      sx={btnProps}
      selected={active}
      onClick={() => onClick(name)}
      onDoubleClick={() => onDblClick(name)}
      ref={ref}
      data-name={name}
    >
      <Box display="flex" alignItems="center" ref={drag} sx={dragProps}>
        <Typography color={errored ? 'error' : 'text.primary'} variant="body2">
          {label}
        </Typography>
        <IconButton
          onClick={directionClickHandler}
          title={hints[direction]}
          size="small"
        >
          <FontAwesomeIcon
            icon={icons[direction]}
          />
        </IconButton>
      </Box>
    </ListItemButton>
  );
}

OrdersItem.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  onDblClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  onHover: PropTypes.func.isRequired,
  padded: PropTypes.bool,
  direction: PropTypes.oneOf(Object.values(DIRECTIONS)).isRequired,
  onDirectionChange: PropTypes.func.isRequired,
  errored: PropTypes.bool,
};

OrdersItem.defaultProps = {
  label: '???',
  active: false,
  padded: false,
  errored: false,
};

export default memo(OrdersItem);
